import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, fromEvent, map, merge, of } from 'rxjs';
import { ToolsService } from './tools.service';
import { environment } from 'src/environments/environment';

/**
 * Owns the integration with the embedded Cicerone v3 chatbot widget
 * (loaded via cicerone-chat.js with `totem="true"`).
 *
 * Responsibilities:
 *  - Inject the loader script for devices configured with a chatbot.
 *  - Keep the widget language aligned with the kiosk.
 *  - Gate launcher visibility on standby/reels and connectivity.
 *
 * Text entry is owned entirely by the widget in totem mode (it renders its
 * own on-screen keyboard); the kiosk does not bridge the keyboard.
 *
 * All access to the cross-origin widget goes through the loader's
 * `window.CiceroneChatEmbed` API; this service is the only place that
 * touches it.
 */
@Injectable({ providedIn: 'root' })
export class ChatbotBridgeService {
  private router = inject(Router);
  private tools = inject(ToolsService);
  private translate = inject(TranslateService);

  private static readonly SCRIPT_ID = 'cicerone-chat-script';
  private static readonly SCRIPT_SRC = `${environment.apiUrlBase}/cicerone-chat.js`;
  // Viewports at or below this width are treated as mobile (non-totem mode).
  private static readonly MOBILE_MAX_WIDTH = 550;

  private standby = false;
  private wasStandby = false;
  private online = navigator.onLine;
  private started = false;

  private get embed(): any {
    return (window as any).CiceroneChatEmbed;
  }

  init(): void {
    if (this.started) return;
    this.started = true;

    this.connectEmbed();
    this.watchRoutes();
    this.watchConnectivity();
    this.watchLanguage();
  }

  // Inject the loader script once the device payload confirms a chatbot is
  // configured. The script is no longer hardcoded in index.html, so it only
  // loads on devices whose `chatBot` id matches. Safe to call repeatedly.
  //
  // Totem mode is selected by viewport width, measured once here: a totem-sized
  // viewport (> 550px) gets `totem="true"` so the widget renders its own
  // on-screen keyboard; a mobile viewport (<= 550px) gets `totem="false"` so the
  // widget uses the device's native keyboard. The loader reads the attribute
  // only at init, so the mode is fixed at injection (no resize re-evaluation).
  //
  // We evaluate the breakpoint with matchMedia (the layout viewport) rather than
  // window.innerWidth: on iOS Safari innerWidth tracks the visual viewport and
  // can read wider than the layout viewport, wrongly selecting totem mode on a
  // phone. matchMedia matches the layout viewport via the CSS engine.
  mount(chatbotId: string, lang: string): void {
    if (document.getElementById(ChatbotBridgeService.SCRIPT_ID)) return;

    const isMobile = window.matchMedia(
      `(max-width: ${ChatbotBridgeService.MOBILE_MAX_WIDTH}px)`,
    ).matches;
    const totem = !isMobile;

    const script = document.createElement('script');
    script.id = ChatbotBridgeService.SCRIPT_ID;
    script.src = ChatbotBridgeService.SCRIPT_SRC;
    script.setAttribute('chatbot-id', chatbotId);
    script.setAttribute('totem', String(totem));
    script.setAttribute('lang-code', lang);
    document.head.appendChild(script);
  }

  // Keep the embedded widget aligned with the kiosk language. We listen to
  // TranslateService.onLangChange (not ToolsService.changeLanguage) because
  // several components switch language by calling translate.use() directly.
  // Updating the loader script tag's `lang-code` lets its MutationObserver
  // propagate the language into the widget.
  private watchLanguage(): void {
    if (this.translate.currentLang) {
      this.setScriptLang(this.translate.currentLang);
    }

    this.translate.onLangChange.subscribe((e) => this.setScriptLang(e.lang));
  }

  private setScriptLang(lang: string): void {
    document.getElementById(ChatbotBridgeService.SCRIPT_ID)?.setAttribute('lang-code', lang);
  }

  setStandby(value: boolean): void {
    this.standby = value;
    this.refreshVisibility();
  }

  private connectEmbed(): void {
    const ready = () => {
      const embed = this.embed;
      if (!embed) return false;

      this.refreshVisibility();
      return true;
    };

    if (ready()) return;

    const interval = setInterval(() => {
      if (ready()) clearInterval(interval);
    }, 300);
  }

  private watchRoutes(): void {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        const url = e.urlAfterRedirects;
        this.standby = url.includes('slides') || url.includes('license');
        this.refreshVisibility();
      });
  }

  private watchConnectivity(): void {
    merge(of(null), fromEvent(window, 'online'), fromEvent(window, 'offline'))
      .pipe(map(() => navigator.onLine))
      .subscribe((status) => {
        this.online = status;
        this.refreshVisibility();
      });
  }

  private refreshVisibility(): void {
    // Detect the standby -> interactive edge before updating history, so a
    // connectivity flap (online/offline) never counts as leaving standby.
    const leavingStandby = this.wasStandby && !this.standby;
    this.wasStandby = this.standby;

    const embed = this.embed;
    if (!embed) return;

    // During standby/reels, hide every widget surface (launcher + open panel +
    // toast) so nothing overlays the attract-loop.
    if (this.standby) {
      this.collapseWidget();
      return;
    }

    // Interactive but offline: launcher-only hide (assistant is online-only).
    if (!this.online || this.tools.isOffline) {
      embed.hide?.();
      return;
    }

    // Returning to the interactive kiosk: reset the previous visitor's
    // conversation (no-ops until the widget exposes reset()), then show.
    if (leavingStandby && typeof embed.reset === 'function') {
      embed.reset();
    }
    embed.show?.();
  }

  // Fully collapse the widget using the loader's public API only: close an open
  // panel (which also clears the toast and restores the launcher icon), clear a
  // standalone toast, then hide the launcher.
  private collapseWidget(): void {
    const embed = this.embed;
    if (!embed) return;

    if (embed.isOpen) embed.toggle?.();
    embed.clearToast?.();
    embed.hide?.();
  }
}
